import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { cn } from '@/utils/cn';
import { monotoneCubicPath, type PathCoord } from '@/utils/chartPaths';
import {
  campaignActions,
  formatDay,
  formatDelta,
  formatReportMoney,
  formatRoi,
  formatRoas,
  formatShare,
  formatUnits,
  headlinePlatforms,
  latestDayPair,
  mergeDays,
  mergeProducts,
  mergeTactics,
  monthStart,
  percentChange,
  platformSnapshot,
  reportTitle,
  roas,
  snapshotTitle,
  summaryParts,
  sumDays,
  tablePlatforms,
  TACTIC_LABELS,
  visibleTactics,
  windowLabel,
  type PlatformSnapshot,
  type SnapshotTotals,
  type BrandAdsDay,
  type BrandAdsMarketplace,
  type BrandAdsPlatform,
  type BrandAdsTacticId,
  type MarketplaceFilter,
} from '@/utils/brandAdsReport';
import { brandsApi } from '@/api/client';

const FILTERS: Array<{ id: MarketplaceFilter; label: string }> = [
  { id: 'all', label: 'Both' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'flipkart', label: 'Flipkart' },
];

const SERIES_COLOR: Record<BrandAdsMarketplace, string> = {
  amazon: '#2563eb',
  flipkart: '#ea580c',
};

type SortKey = 'name' | 'spend' | 'sales' | 'units' | 'roas';
type SortDir = 'asc' | 'desc';

const TACTIC_NOTE =
  'Brand, Competition, and Generic are campaign tags. Rest is spend on campaigns with no tag.';

const SNAP_NOTE = 'Yesterday is the selected end date. MTD runs from the 1st of that month. ROI is GMS ÷ spend.';

async function loadBrandAdsReport(brandId: string, startDate: string, endDate: string) {
  const res = await brandsApi.getAdsReport(brandId, { startDate, endDate });
  if (!res.success || !res.data) throw new Error('Failed to load the report');
  return res.data;
}

const TACTIC_RANK: Record<BrandAdsTacticId, number> = {
  brand: 0,
  competitor: 1,
  generic: 2,
  rest: 3,
};

type MoneyRow = {
  name: string;
  spend: number;
  sales: number;
  units: number;
  date?: string;
  tacticId?: BrandAdsTacticId;
};

export function BrandAdsReport({
  brandId,
  brandName,
  startDate,
  endDate,
  isDemo,
}: {
  brandId: string;
  brandName: string;
  startDate: string;
  endDate: string;
  isDemo: boolean;
}) {
  const [filter, setFilter] = useState<MarketplaceFilter>('all');
  const mtdStart = monthStart(endDate);
  const reportQuery = useQuery({
    queryKey: ['brand-ads-report', brandId, startDate, endDate],
    queryFn: () => loadBrandAdsReport(brandId, startDate, endDate),
    enabled: Boolean(brandId && startDate && endDate) && !isDemo,
  });
  const mtdQuery = useQuery({
    queryKey: ['brand-ads-report', brandId, mtdStart, endDate],
    queryFn: () => loadBrandAdsReport(brandId, mtdStart, endDate),
    enabled: Boolean(brandId && mtdStart && endDate) && !isDemo,
  });

  const platforms = reportQuery.data?.platforms ?? [];
  const cards = headlinePlatforms(platforms, filter);
  const snapPlatforms = headlinePlatforms(mtdQuery.data?.platforms ?? [], filter);
  const tacticRows = visibleTactics(mergeTactics(cards)).map((tactic) => ({
    date: tactic.date,
    tacticId: tactic.id,
    name: TACTIC_LABELS[tactic.id],
    spend: tactic.spend,
    sales: tactic.sales,
    units: tactic.units,
  }));
  const table = tablePlatforms(platforms, filter);
  const days = mergeDays(cards);
  const currency = cards[0]?.currency ?? 'INR';
  const totals = sumDays(days);
  const actions = campaignActions(cards.flatMap((platform) => platform.campaigns));
  const summary = summaryParts(cards);

  return (
    <div className="mx-auto min-h-full max-w-[90rem] space-y-4 bg-page px-4 py-5">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          {reportTitle(brandName, filter)}
        </h2>
        {!isDemo && !reportQuery.isLoading && !reportQuery.isError && cards.length > 0 ? (
          <p className="mt-2 max-w-4xl text-sm leading-6 text-foreground-muted">
            {summary.map((part, index) =>
              part.strong ? (
                <strong key={index} className="font-semibold text-foreground">
                  {part.text}
                </strong>
              ) : (
                <span key={index}>{part.text}</span>
              )
            )}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 shadow-sm" role="group" aria-label="Marketplace">
          {FILTERS.map((option) => {
            const selected = option.id === filter;
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setFilter(option.id)}
                className={cn(
                  'h-7 rounded-md px-2.5 text-xs font-medium transition-colors',
                  selected
                    ? 'bg-brand text-brand-foreground'
                    : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {platforms.length > 0 ? (
          <p className="text-sm text-foreground-muted">Window: {windowLabel(platforms, filter)}</p>
        ) : null}
      </div>

      {isDemo ? (
        <EmptyMessage>No performance data for this period.</EmptyMessage>
      ) : reportQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-foreground-muted">
          <Spinner />
          Loading ads report…
        </div>
      ) : reportQuery.isError ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
          <p className="text-sm text-error-ink">Could not load this report.</p>
          <button
            type="button"
            onClick={() => void reportQuery.refetch()}
            className="mt-3 text-sm font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : platforms.length === 0 ? (
        <EmptyMessage>No performance data for this period.</EmptyMessage>
      ) : cards.length === 0 ? (
        <EmptyMessage>No performance data for this marketplace.</EmptyMessage>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              label="Ad sales"
              value={formatReportMoney(totals.sales, currency, true)}
              detail={deltaCaption(days, 'sales')}
            />
            <KpiCard
              label="Ad spend"
              value={formatReportMoney(totals.spend, currency, true)}
              detail={deltaCaption(days, 'spend')}
            />
            <KpiCard
              label="ROAS (ad sales ÷ spend)"
              value={formatRoas(roas(totals.sales, totals.spend))}
              detail={roasCaption(days)}
            />
            <KpiCard
              label="Units sold from ads"
              value={formatUnits(totals.units)}
              detail={deltaCaption(days, 'units')}
            />
            <KpiCard
              label="Spend with zero sales"
              value={formatReportMoney(actions.zeroSaleSpend, currency, true)}
              detail={`${formatShare(actions.zeroSaleSpend, totals.spend)} of spend, on campaigns with no sales in the window`}
            />
          </div>

          <Card className="p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              {filter === 'all' ? 'Amazon vs Flipkart' : 'Platform'}
            </h3>
            <p className="mt-1 text-xs text-foreground-muted">
              Same window as above. Each marketplace credits sales differently, so compare trends within a platform rather than ROAS across them.
            </p>
            <TableWrap className="mt-3">
              <Table compact>
                <TableHeader>
                  <TableRow>
                    {['Platform', 'Spend', 'Ad sales', 'Units', 'ROAS', 'Zero-sale spend', 'Share'].map((heading) => (
                      <TableHead key={heading}>{heading}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <tbody>
                  {table.map((platform) => {
                    const metrics = sumDays(platform.days);
                    const rowActions = campaignActions(platform.campaigns);
                    return (
                      <TableRow key={`${platform.marketplace}-${platform.currency}`}>
                        <TableCell compact className="font-medium">
                          {platform.label}
                        </TableCell>
                        <TableCell compact>{formatReportMoney(metrics.spend, platform.currency)}</TableCell>
                        <TableCell compact>{formatReportMoney(metrics.sales, platform.currency)}</TableCell>
                        <TableCell compact>{formatUnits(metrics.units)}</TableCell>
                        <TableCell compact>{formatRoas(roas(metrics.sales, metrics.spend))}</TableCell>
                        <TableCell compact>
                          {formatReportMoney(rowActions.zeroSaleSpend, platform.currency)}
                        </TableCell>
                        <TableCell compact>{formatShare(rowActions.zeroSaleSpend, metrics.spend)}</TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </Card>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Daily trend</h3>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              <Card className="p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-foreground">Ad sales by day</h4>
                <p className="mt-1 text-xs text-foreground-muted">{salesChartNote(cards)}</p>
                <SeriesLegend platforms={cards} />
                <SalesBars platforms={cards} currency={currency} />
              </Card>
              <Card className="p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-foreground">ROAS by day</h4>
                <p className="mt-1 text-xs text-foreground-muted">
                  Ad sales ÷ spend for each day. A falling line means each rupee is buying less as the sale goes on.
                </p>
                <SeriesLegend platforms={cards} />
                <RoasLines platforms={cards} />
              </Card>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Where the money goes</h3>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              <MoneyTable
                title="By product"
                note="Bar shows share of spend. Click a header to sort."
                currency={currency}
                rows={mergeProducts(cards).map((product) => ({
                  name: product.name,
                  spend: product.spend,
                  sales: product.sales,
                  units: product.units,
                }))}
              />
              <SnapshotTable
                endDate={endDate}
                platforms={snapPlatforms}
                isLoading={mtdQuery.isLoading}
                isError={mtdQuery.isError}
                onRetry={() => void mtdQuery.refetch()}
              />
              <TacticTable className="xl:col-span-2" currency={currency} rows={tacticRows} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function EmptyMessage({ children }: { children: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-10 text-center">
      <p className="text-sm text-foreground-muted">{children}</p>
    </div>
  );
}

function KpiCard({ label, value, detail }: { label: string; value: string; detail: string | null }) {
  const negative = detail?.trim().startsWith('-') ?? false;
  const positive = detail?.trim().startsWith('+') ?? false;
  return (
    <Card className="p-4 shadow-sm">
      <p className="text-sm text-foreground-muted">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{value}</p>
      {detail ? (
        <p
          className={cn(
            'mt-2 text-xs',
            negative ? 'text-error-ink' : positive ? 'text-success-ink' : 'text-foreground-muted'
          )}
        >
          {detail}
        </p>
      ) : (
        <p className="mt-2 text-xs text-foreground-muted">—</p>
      )}
    </Card>
  );
}

function deltaCaption(days: BrandAdsDay[], kind: 'sales' | 'spend' | 'units'): string | null {
  const { latest, previous } = latestDayPair(days);
  if (!latest || !previous) return null;
  const formatted = formatDelta(percentChange(latest[kind], previous[kind]));
  if (!formatted) return null;
  return `${formatted} on ${formatDay(latest.date)} vs day before`;
}

function roasCaption(days: BrandAdsDay[]): string | null {
  const { latest, previous } = latestDayPair(days);
  if (!latest || !previous) return null;
  const current = roas(latest.sales, latest.spend);
  const prior = roas(previous.sales, previous.spend);
  if (current == null || prior == null) return null;
  const formatted = formatDelta(percentChange(current, prior));
  if (!formatted) return null;
  return `${formatted} on ${formatDay(latest.date)} vs day before`;
}

function salesChartNote(platforms: BrandAdsPlatform[]): string {
  const flipkart = platforms.find((platform) => platform.marketplace === 'flipkart');
  const missing = flipkart?.days.filter((day) => !day.hasRows) ?? [];
  if (missing.length === 0) return 'Bars per platform.';
  const listed = missing.slice(0, 3).map((day) => formatDay(day.date));
  const extra = missing.length > 3 ? `, and ${missing.length - 3} other days` : '';
  return `Bars per platform. Flipkart has no spend or sales for ${listed.join(', ')}${extra}, so that bar is missing.`;
}

function SeriesLegend({ platforms }: { platforms: BrandAdsPlatform[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-3 text-xs text-foreground-muted">
      {platforms.map((platform) => (
        <span key={`${platform.marketplace}-${platform.currency}`} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: SERIES_COLOR[platform.marketplace] }}
            aria-hidden
          />
          {platform.label}
        </span>
      ))}
    </div>
  );
}

const CHART_W = 560;
const CHART_H = 210;
const PAD = { top: 16, right: 16, bottom: 32, left: 56 };

function axisDates(platforms: BrandAdsPlatform[]): string[] {
  const dates = new Set<string>();
  for (const platform of platforms) {
    for (const day of platform.days) dates.add(day.date);
  }
  return [...dates].sort();
}

function SalesBars({ platforms, currency }: { platforms: BrandAdsPlatform[]; currency: string }) {
  const dates = axisDates(platforms);
  const max = Math.max(
    1,
    ...platforms.flatMap((platform) => platform.days.map((day) => (day.hasRows ? day.sales : 0)))
  );
  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const slot = dates.length > 0 ? innerW / dates.length : innerW;
  const barWidth = Math.max(4, Math.min(18, (slot * 0.7) / Math.max(platforms.length, 1)));
  const ticks = [max, max / 2, 0];
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="mt-2 w-full text-foreground-muted" role="img" aria-label="Ad sales by day">
      {ticks.map((tick) => {
        const y = PAD.top + innerH - (tick / max) * innerH;
        return (
          <g key={tick}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.15" />
            <text x={PAD.left - 8} y={y + 3} textAnchor="end" fontSize="10" fill="currentColor">
              {formatReportMoney(tick, currency, true)}
            </text>
          </g>
        );
      })}
      {dates.map((date, index) => {
        const center = PAD.left + slot * index + slot / 2;
        const showLabel = dates.length <= 12 || index % Math.ceil(dates.length / 8) === 0;
        return (
          <g key={date}>
            {platforms.map((platform, seriesIndex) => {
              const day = platform.days.find((item) => item.date === date);
              if (!day?.hasRows) return null;
              const height = (day.sales / max) * innerH;
              const offset = (seriesIndex - (platforms.length - 1) / 2) * (barWidth + 2);
              return (
                <rect
                  key={platform.label}
                  x={center + offset - barWidth / 2}
                  y={PAD.top + innerH - height}
                  width={barWidth}
                  height={Math.max(height, day.sales > 0 ? 1 : 0)}
                  rx="2"
                  fill={SERIES_COLOR[platform.marketplace]}
                >
                  <title>{`${platform.label} ${formatDay(date)} ${formatReportMoney(day.sales, platform.currency)}`}</title>
                </rect>
              );
            })}
            {showLabel ? (
              <text x={center} y={CHART_H - 8} textAnchor="middle" fontSize="10" fill="currentColor">
                {formatDay(date)}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function RoasLines({ platforms }: { platforms: BrandAdsPlatform[] }) {
  const dates = axisDates(platforms);
  const values = platforms.flatMap((platform) =>
    platform.days.flatMap((day) => {
      if (!day.hasRows) return [];
      const value = roas(day.sales, day.spend);
      return value == null ? [] : [value];
    })
  );
  const max = Math.max(1, ...values);
  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const xAt = (index: number) =>
    PAD.left + (dates.length <= 1 ? innerW / 2 : (index / (dates.length - 1)) * innerW);
  const yAt = (value: number) => PAD.top + innerH - (value / max) * innerH;
  const ticks = [max, max / 2, 0];
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="mt-2 w-full text-foreground-muted" role="img" aria-label="ROAS by day">
      {ticks.map((tick) => {
        const y = yAt(tick);
        return (
          <g key={tick}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.15" />
            <text x={PAD.left - 8} y={y + 3} textAnchor="end" fontSize="10" fill="currentColor">
              {formatRoas(tick)}
            </text>
          </g>
        );
      })}
      {platforms.map((platform) => {
        const points = dates.map((date, index) => {
          const day = platform.days.find((item) => item.date === date);
          if (!day?.hasRows) return null;
          const value = roas(day.sales, day.spend);
          if (value == null) return null;
          return { x: xAt(index), y: yAt(value), value, date };
        });
        const runs = contiguous(points);
        const last = [...points].reverse().find((point) => point != null);
        return (
          <g key={platform.label}>
            {runs.map((run, index) => (
              <path
                key={index}
                d={monotoneCubicPath(run)}
                fill="none"
                stroke={SERIES_COLOR[platform.marketplace]}
                strokeWidth="2"
              />
            ))}
            {points.map((point) =>
              point ? (
                <circle key={point.date} cx={point.x} cy={point.y} r="3" fill={SERIES_COLOR[platform.marketplace]}>
                  <title>{`${platform.label} ${formatDay(point.date)} ${formatRoas(point.value)}`}</title>
                </circle>
              ) : null
            )}
            {last ? (
              <text
                x={last.x + (last.x > CHART_W - 90 ? -8 : 8)}
                y={last.y - 8}
                textAnchor={last.x > CHART_W - 90 ? 'end' : 'start'}
                fontSize="10"
                fill={SERIES_COLOR[platform.marketplace]}
              >
                {`${platform.marketplace === 'amazon' ? 'Amazon' : 'Flipkart'} ${formatRoas(last.value)}`}
              </text>
            ) : null}
          </g>
        );
      })}
      {dates.map((date, index) => {
        const showLabel = dates.length <= 12 || index % Math.ceil(dates.length / 8) === 0;
        if (!showLabel) return null;
        return (
          <text key={date} x={xAt(index)} y={CHART_H - 8} textAnchor="middle" fontSize="10" fill="currentColor">
            {formatDay(date)}
          </text>
        );
      })}
    </svg>
  );
}

function contiguous(points: Array<(PathCoord & { value: number; date: string }) | null>): PathCoord[][] {
  const runs: PathCoord[][] = [];
  let current: PathCoord[] = [];
  for (const point of points) {
    if (!point) {
      if (current.length > 0) runs.push(current);
      current = [];
      continue;
    }
    current.push({ x: point.x, y: point.y });
  }
  if (current.length > 0) runs.push(current);
  return runs;
}

type TacticMetrics = {
  spend: number;
  sales: number;
  units: number;
};

type TacticDay = {
  date: string;
  byTactic: Map<BrandAdsTacticId, TacticMetrics>;
  total: TacticMetrics;
};

function TacticTable({
  currency,
  rows,
  className,
}: {
  currency: string;
  rows: MoneyRow[];
  className?: string;
}) {
  const columns = useMemo(() => tacticColumns(rows), [rows]);
  const totals = useMemo(() => mixTotals(rows, columns), [rows, columns]);
  const days = useMemo(() => dayGroups(rows), [rows]);
  const windowSpend = columns.reduce((sum, id) => sum + (totals.get(id)?.spend ?? 0), 0);
  return (
    <Card className={cn('p-4 shadow-sm', className)}>
      <h4 className="text-sm font-semibold text-foreground">By tactic</h4>
      <p className="mt-1 text-xs text-foreground-muted">{TACTIC_NOTE}</p>
      {columns.length === 0 ? (
        <p className="mt-3 text-xs text-foreground-muted">No spend in this window.</p>
      ) : (
        <>
          <div
            className={cn(
              'mt-3 grid gap-2',
              columns.length >= 4
                ? 'grid-cols-2 xl:grid-cols-4'
                : columns.length === 3
                  ? 'grid-cols-1 sm:grid-cols-3'
                  : columns.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
            )}
          >
            {columns.map((id) => {
              const metrics = totals.get(id);
              if (!metrics) return null;
              return (
                <div key={id} className="rounded-lg border border-border px-3 py-2.5">
                  <p className="text-xs font-medium text-foreground-muted">{TACTIC_LABELS[id]}</p>
                  <p className="mt-1 text-lg font-semibold tracking-tight text-foreground">
                    {formatRoas(roas(metrics.sales, metrics.spend))}
                  </p>
                  <p className="mt-1 text-xs text-foreground">
                    {formatReportMoney(metrics.spend, currency)} · {formatShare(metrics.spend, windowSpend)} of spend
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-muted">
                    {formatReportMoney(metrics.sales, currency)} ad sales · {formatUnits(metrics.units)} units
                  </p>
                </div>
              );
            })}
          </div>
          {days.length > 0 ? (
            <TableWrap className="mt-3">
              <Table compact>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    {columns.map((id) => (
                      <TableHead key={id}>{TACTIC_LABELS[id]}</TableHead>
                    ))}
                    <TableHead className="border-l border-border">Day</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {days.map((day) => (
                    <TableRow key={day.date}>
                      <TableCell compact className="align-top font-medium">
                        {formatDay(day.date)}
                      </TableCell>
                      {columns.map((id) => (
                        <TacticMetricCell
                          key={id}
                          metrics={day.byTactic.get(id) ?? null}
                          currency={currency}
                          daySpend={day.total.spend}
                        />
                      ))}
                      <TacticMetricCell className="border-l border-border" metrics={day.total} currency={currency} />
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          ) : null}
        </>
      )}
    </Card>
  );
}

function TacticMetricCell({
  metrics,
  currency,
  daySpend,
  className,
}: {
  metrics: TacticMetrics | null;
  currency: string;
  daySpend?: number;
  className?: string;
}) {
  if (!metrics) {
    return <TableCell compact className={cn('align-top text-foreground-muted', className)}>—</TableCell>;
  }
  return (
    <TableCell compact className={cn('min-w-[10rem] align-top', className)}>
      <p className="text-sm font-semibold text-foreground">{formatRoas(roas(metrics.sales, metrics.spend))}</p>
      <p className="mt-1">
        {formatReportMoney(metrics.spend, currency)}
        {daySpend != null ? <span className="text-foreground-muted"> · {formatShare(metrics.spend, daySpend)}</span> : null}
      </p>
      {daySpend != null ? <SpendShare spend={metrics.spend} total={daySpend} wide /> : null}
      <p className="mt-1 text-foreground-muted">
        {formatReportMoney(metrics.sales, currency)} ad sales · {formatUnits(metrics.units)} units
      </p>
    </TableCell>
  );
}

function tacticColumns(rows: MoneyRow[]): BrandAdsTacticId[] {
  const ids = new Set<BrandAdsTacticId>();
  for (const row of rows) {
    if (row.tacticId) ids.add(row.tacticId);
  }
  return [...ids].sort((left, right) => TACTIC_RANK[left] - TACTIC_RANK[right]);
}

function mixTotals(rows: MoneyRow[], columns: BrandAdsTacticId[]): Map<BrandAdsTacticId, TacticMetrics> {
  const totals = new Map<BrandAdsTacticId, TacticMetrics>();
  for (const id of columns) totals.set(id, { spend: 0, sales: 0, units: 0 });
  for (const row of rows) {
    if (!row.tacticId) continue;
    const current = totals.get(row.tacticId);
    if (!current) continue;
    current.spend += row.spend;
    current.sales += row.sales;
    current.units += row.units;
  }
  return totals;
}

function dayGroups(rows: MoneyRow[]): TacticDay[] {
  const byDate = new Map<string, TacticDay>();
  for (const row of rows) {
    if (!row.date || !row.tacticId) continue;
    let day = byDate.get(row.date);
    if (!day) {
      day = { date: row.date, byTactic: new Map(), total: { spend: 0, sales: 0, units: 0 } };
      byDate.set(row.date, day);
    }
    const current = day.byTactic.get(row.tacticId) ?? { spend: 0, sales: 0, units: 0 };
    current.spend += row.spend;
    current.sales += row.sales;
    current.units += row.units;
    day.byTactic.set(row.tacticId, current);
    day.total.spend += row.spend;
    day.total.sales += row.sales;
    day.total.units += row.units;
  }
  return [...byDate.values()].sort((left, right) => right.date.localeCompare(left.date));
}

const SNAP_ROWS: Array<{
  label: string;
  value: (totals: SnapshotTotals, currency: string) => string;
}> = [
  {
    label: 'Total Spent',
    value: (totals, currency) => formatReportMoney(totals.spend, currency),
  },
  {
    label: 'Units Sold',
    value: (totals) => formatUnits(totals.units),
  },
  {
    label: 'GMS',
    value: (totals, currency) => formatReportMoney(totals.sales, currency),
  },
  {
    label: 'ROI',
    value: (totals) => formatRoi(roas(totals.sales, totals.spend)),
  },
];

function SnapshotTable({
  endDate,
  platforms,
  isLoading,
  isError,
  onRetry,
}: {
  endDate: string;
  platforms: BrandAdsPlatform[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const columns = platforms.map((platform) => ({
    key: `${platform.marketplace}-${platform.currency}`,
    label: platform.label,
    currency: platform.currency,
    snap: platformSnapshot(platform, endDate),
  }));
  return (
    <Card className="p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-foreground">{snapshotTitle(endDate)}</h4>
      <p className="mt-1 text-xs text-foreground-muted">{SNAP_NOTE}</p>
      {isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-foreground-muted">
          <Spinner />
          Loading snapshot…
        </div>
      ) : isError ? (
        <div className="mt-3">
          <p className="text-xs text-error-ink">Could not load this snapshot.</p>
          <button type="button" onClick={onRetry} className="mt-2 text-xs font-semibold text-brand hover:underline">
            Retry
          </button>
        </div>
      ) : columns.length === 0 ? (
        <p className="mt-3 text-xs text-foreground-muted">No performance data for this marketplace.</p>
      ) : (
        <TableWrap className="mt-3">
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHead className="static">Metrics</TableHead>
                {columns.map((column, index) => (
                  <TableHead
                    key={column.key}
                    colSpan={2}
                    className={cn('static text-center', index > 0 && 'border-l border-border')}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
              <TableRow>
                <TableHead className="static" />
                {columns.map((column, index) => (
                  <SnapshotPeriodHeads key={column.key} divider={index > 0} />
                ))}
              </TableRow>
            </TableHeader>
            <tbody>
              {SNAP_ROWS.map((row) => (
                <TableRow key={row.label}>
                  <TableCell compact className="font-medium">
                    {row.label}
                  </TableCell>
                  {columns.map((column, index) => (
                    <SnapshotPair
                      key={column.key}
                      snap={column.snap}
                      currency={column.currency}
                      format={row.value}
                      divider={index > 0}
                    />
                  ))}
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Card>
  );
}

function SnapshotPeriodHeads({ divider }: { divider: boolean }) {
  return (
    <>
      <TableHead className={cn('static', divider && 'border-l border-border')}>Yesterday</TableHead>
      <TableHead className="static">MTD</TableHead>
    </>
  );
}

function SnapshotPair({
  snap,
  currency,
  format,
  divider,
}: {
  snap: PlatformSnapshot;
  currency: string;
  format: (totals: SnapshotTotals, currency: string) => string;
  divider: boolean;
}) {
  return (
    <>
      <TableCell compact className={cn(divider && 'border-l border-border')}>
        {snap.yesterday.hasRows ? format(snap.yesterday, currency) : '—'}
      </TableCell>
      <TableCell compact>{snap.mtd.hasRows ? format(snap.mtd, currency) : '—'}</TableCell>
    </>
  );
}

function MoneyTable({
  title,
  note,
  currency,
  rows,
}: {
  title: string;
  note: string;
  currency: string;
  rows: MoneyRow[];
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'spend', dir: 'desc' });
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((left, right) => compareRows(left, right, sort.key, sort.dir));
    return copy;
  }, [rows, sort]);
  const totalSpend = rows.reduce((sum, row) => sum + row.spend, 0);
  const toggle = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );
  };
  return (
    <Card className="p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <p className="mt-1 text-xs text-foreground-muted">{note}</p>
      <TableWrap compact className="mt-3">
        <Table compact>
          <TableHeader>
            <TableRow>
              <SortHead label="Product" sortKey="name" sort={sort} onSort={toggle} />
              <SortHead label="Spend" sortKey="spend" sort={sort} onSort={toggle} />
              <SortHead label="Ad sales" sortKey="sales" sort={sort} onSort={toggle} />
              <SortHead label="Units" sortKey="units" sort={sort} onSort={toggle} />
              <SortHead label="ROAS" sortKey="roas" sort={sort} onSort={toggle} />
            </TableRow>
          </TableHeader>
          <tbody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell compact colSpan={5} className="text-foreground-muted">
                  No spend in this window.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => (
                <TableRow key={row.name}>
                  <TableCell compact className="font-medium">
                    {row.name}
                  </TableCell>
                  <TableCell compact>
                    <SpendShare spend={row.spend} total={totalSpend} />
                    {formatReportMoney(row.spend, currency)}
                  </TableCell>
                  <TableCell compact>{formatReportMoney(row.sales, currency)}</TableCell>
                  <TableCell compact>{formatUnits(row.units)}</TableCell>
                  <TableCell compact>{formatRoas(roas(row.sales, row.spend))}</TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
}

function SortHead({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <TableHead aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="uppercase tracking-[0.12em]" onClick={() => onSort(sortKey)}>
        {label}
        {active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </TableHead>
  );
}

function SpendShare({ spend, total, wide = false }: { spend: number; total: number; wide?: boolean }) {
  const width = total > 0 ? Math.min(100, (spend / total) * 100) : 0;
  return (
    <span
      className={cn(
        'overflow-hidden rounded-full bg-surface-muted align-middle',
        wide ? 'mt-1 block h-1.5 w-28' : 'mr-2 inline-block h-1.5 w-14'
      )}
    >
      <span className="block h-full rounded-full bg-foreground/40" style={{ width: `${width}%` }} />
    </span>
  );
}

function compareRows(left: MoneyRow, right: MoneyRow, key: SortKey, dir: SortDir): number {
  const leftValue = key === 'name' ? left.name : key === 'roas' ? (roas(left.sales, left.spend) ?? -1) : left[key];
  const rightValue = key === 'name' ? right.name : key === 'roas' ? (roas(right.sales, right.spend) ?? -1) : right[key];
  const order =
    typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
  return dir === 'asc' ? order : -order;
}
